/* eslint-disable no-use-before-define */

import { useCallback, useMemo, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useFormik } from "formik";

// @material-ui/core components
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import LinearProgress from "@material-ui/core/LinearProgress";
import Grid from "@material-ui/core/Grid";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";

// Karsten
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import MaskAndValid from "utils/maskAndValid";
import Api from "lib/api/httpRequestHelper";
import AutoCompleteField from "components/_refactor/fields/autocomplete";

const LSBrands = JSON.parse(localStorage.getItem("usuarioMarcas")) || [];

const getDefaultBrandValue = (idMarca, options) => {
  return options.find((option) => option.IdMarca === idMarca);
};

export default function FormIntegrationDialog({
  open,
  formType,
  onClose,
  selectedIntegration,
  systemIntegrations,
}) {
  const brands = useMemo(() => LSBrands, []);
  const [submitting, setSubmitting] = useState(false);
  const [marca, setMarca] = useState(
    getDefaultBrandValue(selectedIntegration?.IdMarca, brands)
  );
  const snackbar = useSnackbar();

  const submit = useCallback(
    async (values, { resetForm }) => {
      const handleClose = (updatedIntegration) => {
        resetForm();
        onClose(updatedIntegration);
      };

      const { sistema, infoPublica, chave, valor } = values;
      const updatedIntegration = {};

      setSubmitting(true);

      return await Api.postParams(
        formType === "edit"
          ? "update-configuration-integration"
          : "create-configuration-integration",
        "adminportal",
        "?valor=" +
          valor +
          "&infoPublica=" +
          infoPublica +
          (formType === "create"
            ? "&chave=" +
              chave +
              "&sistema=" +
              sistema +
              "&idMarca=" +
              marca.IdMarca
            : "&idIntegracaoConfiguracao=" +
              selectedIntegration.IdIntegracaoConfiguracao.toString()),
        true
      )
        .then((res) => {
          if (res.Success) {
            snackbar.showMessage(
              `Integração ${
                formType === "edit" ? "atualizada" : "criada"
              } com sucesso!`,
              {
                severity: "success",
              }
            );
            setSubmitting(false);
            handleClose({ ...updatedIntegration, ...res.Result });
          } else {
            snackbar.showMessage(
              `Ocorreu um erro ao ${
                formType === "edit" ? "atualizar" : "criar"
              } a integração. Verifique os parâmetros informados.`,
              {
                severity: "error",
              }
            );
            setSubmitting(false);
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });
          setSubmitting(false);        });
    },
    [
      formType,
      marca?.IdMarca,
      onClose,
      selectedIntegration?.IdIntegracaoConfiguracao,
      snackbar,
    ]
  );

  const formik = useFormik({
    initialValues: {
      sistema: selectedIntegration?.Sistema || "",
      chave: selectedIntegration?.Chave || "",
      valor: selectedIntegration?.Valor || "",
      infoPublica: selectedIntegration?.InformacaoPublica || false,
    },
    onSubmit: submit,
  });

  const { values, handleChange, handleSubmit, dirty, resetForm } = formik;

  const handleClose = useCallback(
    (updatedIntegration) => {
      resetForm();
      onClose(updatedIntegration);
    },
    [onClose, resetForm]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        maxWidth={"md"}
      >
        <PerfectScrollbar>
          <form onSubmit={handleSubmit}>
            <DialogTitle>
              <span>
                <Typography id="alert-dialog-title" variant="h3" component="h3">
                  {`${
                    formType === "edit"
                      ? "Editar"
                      : formType === "delete"
                      ? "Excluir"
                      : "Criar"
                  } Integração`}
                </Typography>
              </span>
            </DialogTitle>
            <PerfectScrollbar>
              <DialogContent style={{ padding: "1rem 1.5rem 0" }}>
                <Grid container>
                  {formType === "create" ? (
                    <>
                      <Grid item xs={12}>
                        <AutoCompleteField
                          options={brands}
                          onChange={setMarca}
                          label="Marca"
                          optionIdKey="IdMarca"
                          disabled={submitting}
                          loading={submitting}
                          value={marca}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AutoCompleteField
                          label="Sistema"
                          required
                          disabled={submitting}
                          value={
                            systemIntegrations.find(
                              (opt) =>
                                opt.IdSistemaIntegracao === values.sistema
                            ) || null
                          }
                          onChange={(option) =>
                            handleChange({
                              target: {
                                name: "sistema",
                                value: option?.IdSistemaIntegracao || "",
                              },
                            })
                          }
                          options={systemIntegrations}
                          optionLabelKey="NomeSistema"
                          optionIdKey="IdSistemaIntegracao"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormGroup>
                          <FormLabel required>Chave</FormLabel>
                          <TextField
                            name="chave"
                            required
                            fullWidth
                            multiline
                            disabled={submitting}
                            value={values.chave}
                            variant="outlined"
                            onChange={handleChange}
                            onInput={(e) => {
                              e.target.value = e.target.value
                                .toString()
                                .slice(0, 200);
                            }}
                          />
                        </FormGroup>
                      </Grid>
                    </>
                  ) : null}
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormLabel required>Valor</FormLabel>
                      <TextField
                        name="valor"
                        required
                        fullWidth
                        multiline
                        disabled={submitting}
                        value={values.valor}
                        variant="outlined"
                        onChange={handleChange}
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .toString()
                            .slice(0, 200);
                        }}
                      />
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.infoPublica}
                            onChange={handleChange}
                            name="infoPublica"
                            disabled={submitting}
                          />
                        }
                        label={
                          <FormLabel required>Informação Pública?</FormLabel>
                        }
                        labelPlacement="top"
                        style={{ alignItems: "flex-start", marginLeft: 0 }}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
                {submitting && (
                  <div>
                    <span>{`${
                      formType === "create" ? "Criando" : "Atualizando"
                    } integração...`}</span>
                    <LinearProgress />
                  </div>
                )}
              </DialogContent>
            </PerfectScrollbar>
            <DialogActions>
              <Button
                component={Box}
                onClick={handleClose}
                disabled={submitting}
              >
                Cancelar
              </Button>
              {formType === "delete" ? (
                <Button variant="contained" type="submit" disabled={submitting}>
                  Confirmar
                </Button>
              ) : (
                <Button
                  variant="contained"
                  type="submit"
                  disabled={Boolean(
                    submitting ||
                      (formType !== "edit" && !dirty) ||
                      MaskAndValid.isNullOrEmpty(values.sistema) ||
                      MaskAndValid.isNullOrEmpty(values.chave) ||
                      MaskAndValid.isNullOrEmpty(values.valor) ||
                      MaskAndValid.isNullOrEmpty(marca.IdMarca)
                  )}
                >
                  {formType === "edit" ? "Atualizar" : "Criar"}
                </Button>
              )}
            </DialogActions>
          </form>
        </PerfectScrollbar>
      </Dialog>
    </>
  );
}
